import React, { useState, useRef, useContext, useEffect } from "react";
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    arrow,
    shift, size,

    useClick,
    useDismiss,
    useRole,
    useInteractions, FloatingFocusManager
} from '@floating-ui/react';
import { IsMobileContext } from "../../context/contexts";

const closeIcon = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
</svg>;
// Shown in the mobile "more below" affordance - the calendar stacks its months
// vertically, so this tells users to scroll down rather than swipe across.
const downChevron = <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
</svg>;

const PopoverInput = ({ icon, children, inputText, isLoading, onTextUpdate, filterText, isOpen, setIsOpen, disabled, footer, header, panelMaxHeight = 450, panelWidthClass = "", doubleWidth, error }) => {
    const arrowRef = useRef(null);
    const scrollPaneRef = useRef(null);
    const panelRef = useRef(null);
    const wrapRef = useRef(null);
    const isMobile = useContext(IsMobileContext);
    // Whether the mobile sheet has content scrolled out of view below the fold.
    const [canScrollDown, setCanScrollDown] = useState(false);

    // The popover lives inside the widget's shadow root, so its z-index can
    // never escape whatever stacking context the host page wraps the widget
    // in (e.g. olympicholidays.com wraps it at z-index 80, and its island-
    // hopping banner artwork sits at 999 and painted over the open panels).
    // While a panel is open, lift every capping host ancestor above typical
    // page content, and restore the original values on close. 1090 clears
    // page artwork but stays below the site's header/modal layer (9999).
    useEffect(() => {
        if (!isOpen) return;
        const bumped = [];
        let el = wrapRef.current?.getRootNode()?.host?.parentElement;
        while (el && el !== document.body) {
            if (getComputedStyle(el).zIndex !== "auto") {
                bumped.push([el, el.style.zIndex]);
                el.style.zIndex = "1090";
            }
            el = el.parentElement;
        }
        return () => {
            for (const [node, prev] of bumped) {
                node.style.zIndex = prev;
            }
        };
    }, [isOpen]);

    // Prevent body scroll when a mobile bottom-sheet is open
    useEffect(() => {
        if (isMobile && isOpen) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isMobile, isOpen]);

    // Track whether there's more to scroll so the mobile sheet can hint at it.
    // The calendar in particular stacks its months vertically, so without a cue
    // users assume the first month is all there is (or try to swipe sideways).
    // A ResizeObserver covers async content (e.g. dates loading in after open).
    useEffect(() => {
        if (!isMobile || !isOpen) {
            setCanScrollDown(false);
            return;
        }
        const el = scrollPaneRef.current;
        if (!el) return;
        const update = () => {
            setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 8);
        };
        update();
        el.addEventListener("scroll", update, { passive: true });
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => {
            el.removeEventListener("scroll", update);
            ro.disconnect();
        };
    }, [isMobile, isOpen]);

    const floatingOpts = {
        open: isOpen,
        onOpenChange: (a, event, reason) => {
            if (reason == "click") {
                if (a) {
                    setIsOpen(true);
                }
            } else {
                setIsOpen(a);
            }
        },
        whileElementsMounted: autoUpdate,
    };

    if (isMobile) {
        delete scrollPaneRef?.current?.style?.maxHeight;
        floatingOpts.strategy = "fixed";
        floatingOpts.middleware = [{
            name: "originMiddleware",
            fn({ x, y }) {
                return {
                    x: 0,
                    y: 0,
                    reset: true,
                };
            },
        }];
    } else {
        floatingOpts.strategy = "absolute";

        floatingOpts.middleware = [offset(10), shift(), size({
            apply({ availableHeight, elements }) {
                if (availableHeight > panelMaxHeight) {
                    availableHeight = panelMaxHeight;
                }
                // Cap the whole panel (not just the scroll pane) so the pinned
                // footer is always within the viewport - the scroll pane flexes
                // to make room for it.
                const value = `${Math.max(0, availableHeight - 25)}px`;
                if (panelRef.current) {
                    panelRef.current.style.maxHeight = value;
                }
            },
        }), arrow({
            element: arrowRef,
            padding: 5
        })];
    }
    const { refs, floatingStyles, context, middlewareData } = useFloating(floatingOpts);

    const mobileOverlayStyles = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    };

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);

    // Merge all the interactions into prop getters
    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
        role,
    ]);


    var textboxValue = filterText ?? "";
    var textboxPlaceholder = inputText;

    if (disabled) {
        return (<div className="relative w-full flex-1">
            <input type="text" disabled className="form-input form-input--with-icon" placeholder={textboxPlaceholder} value={textboxValue} />
            <span className="absolute left-3 top-[20px] -translate-y-1/2 pointer-events-none text-content_2">
                {icon}
            </span>
        </div>);
    }

    if (isLoading) {
        return (<div className="relative w-full flex-1">
            <input type="text" readOnly className="form-input form-input--with-icon" placeholder="Loading..." value="" />
            <span className="absolute left-3 top-[20px] -translate-y-1/2 pointer-events-none text-content_2">
                {icon}
            </span>
        </div>);
    }
    let isReadOnly = true;
    if (onTextUpdate) {
        isReadOnly = false;
    } else {
        onTextUpdate = (t) => { }
    }

    // Minimum desktop popover width. NOTE: in Tailwind v4 the spacing scale is
    // dynamic, so min-w-172 (43rem/688px) and min-w-100 (25rem/400px) are real
    // widths, not no-ops - without them the popovers collapse to content width
    // and look squished. Mobile is a full-width sheet, so no min width.
    let minWidth = doubleWidth ? "min-w-172" : "min-w-100";
    if (isMobile) {
        minWidth = "";
    }

    return (
        <div ref={wrapRef} className="relative w-full flex-1 z-auto">
            <input readOnly={isReadOnly} type="text" ref={refs.setReference} {...getReferenceProps()} value={textboxValue} title={isReadOnly ? textboxValue : undefined} onChange={(e) => onTextUpdate(e.target.value)} aria-invalid={error ? true : undefined} className={"form-input form-input--with-icon" + (error ? " border-error ring-1 ring-error" : "")} placeholder={textboxPlaceholder} />
            <span className={"absolute left-3 top-[20px] -translate-y-1/2 pointer-events-none " + (error ? "text-error" : "text-content_2")}>
                {icon}
            </span>
            {/* Validation hint, absolutely positioned so it can't shift the
                horizontal field row. */}
            {error && (
                <span role="alert" className="absolute left-0 top-full mt-1 text-xs text-error whitespace-nowrap z-20">
                    {error}
                </span>
            )}
            {isOpen && (
                <FloatingFocusManager context={context} modal={isMobile} initialFocus={isMobile ? -1 : refs.reference}>
                    {isMobile ? (
                        // Mobile: full-screen backdrop + bottom sheet
                        <div
                            ref={refs.setFloating}
                            style={mobileOverlayStyles}
                            {...getFloatingProps()}
                            className="z-50 bg-black/50"
                            onClick={(e) => {
                                if (e.target === e.currentTarget) {
                                    setIsOpen(false);
                                }
                            }}
                        >
                            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[20px] max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
                                {/* Drag handle */}
                                <div className="flex justify-center pt-3 pb-1 shrink-0">
                                    <div className="w-10 h-1 bg-content_4 rounded-full"></div>
                                </div>
                                {/* Close button */}
                                <button
                                    type="button"
                                    aria-label="Close panel"
                                    onClick={() => setIsOpen(false)}
                                    className="absolute top-3 right-4 text-content_2 hover:text-primary p-1"
                                >
                                    {closeIcon}
                                </button>
                                {header != null && (
                                    <div className="px-4 pt-2 pb-2 shrink-0">
                                        {header}
                                    </div>
                                )}
                                <div className="relative flex-1 min-h-0 flex flex-col">
                                    <div ref={scrollPaneRef} className="overflow-y-auto flex-1 px-4 pb-2"
                                        onClickCapture={(e) => {
                                            // Dismiss the on-screen keyboard when a row is
                                            // tapped - with the keyboard up it covers the
                                            // sheet footer, so Ok looks like it vanished.
                                            // getRootNode() because we render in a shadow DOM.
                                            const active = e.currentTarget.getRootNode()?.activeElement;
                                            if (active && active.tagName == "INPUT" && active.type == "text" && !e.target.closest("input")) {
                                                active.blur();
                                            }
                                        }}>
                                        {children}
                                    </div>
                                    {/* "More below" hint: a fade + bouncing chevron that
                                        only shows while there's content below the fold,
                                        so it points down (scroll) rather than across. */}
                                    {canScrollDown && (
                                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 flex items-end justify-center pb-1"
                                            style={{ background: "linear-gradient(to top, #ffffff 35%, rgba(255,255,255,0))" }}>
                                            <span className="animate-bounce text-content_2">
                                                {downChevron}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {footer != null && (
                                    <div className="px-4 py-3 border-t border-content_4 shrink-0">
                                        {footer}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        // Desktop: floating popover
                        <div
                            ref={refs.setFloating}
                            style={floatingStyles}
                            {...getFloatingProps()}
                            className={"z-[9999] " + minWidth}
                        >
                            <div
                                ref={arrowRef}
                                className="h-4 w-4 bg-white border border-white border-t-content_4 border-l-content_4 rounded-[4px]"
                                style={{
                                    position: 'absolute',
                                    left: middlewareData.arrow?.x,
                                    top: middlewareData.arrow?.y,
                                    transform: "translate(0px, -8px) rotate(45deg)",
                                }}
                            />
                            <div ref={panelRef} className={"bg-white p-2 rounded-[8px] pl-3 pr-2 border border-content_4 shadow-2xl flex flex-col overflow-hidden " + panelWidthClass}>
                                {header != null && (
                                    <div className="shrink-0">
                                        {header}
                                    </div>
                                )}
                                <div ref={scrollPaneRef} className="overflow-y-auto flex-1 min-h-0">
                                    {children}
                                </div>
                                {footer != null && (
                                    <div className="shrink-0 pt-2 mt-1 border-t border-content_4">
                                        {footer}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </FloatingFocusManager>
            )}
        </div>);
}
export default PopoverInput;
